/**
 * Generic API fetch wrapper for the Level Awakening API.
 *
 * Three jobs:
 *   1. Prefix paths with VITE_API_BASE_URL and always send credentials so the
 *      httpOnly refreshToken cookie rides along.
 *   2. Auto-attach `Authorization: Bearer <accessToken>` from the in-memory
 *      tokenStore when present.
 *   3. On 401, transparently refresh once (deduped per tab, serialized across
 *      tabs via the refresh lock) and replay the original request.
 *
 * /auth/refresh and /auth/logout are called directly via authClient.ts, not
 * through this wrapper — they would recurse on 401.
 */
import { postRefresh } from './auth/authClient';
import { withRefreshLock } from './auth/refreshLock';
import { getAccessToken, setAccessToken } from './auth/tokenStore';
import type { AuthUser } from './auth/types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** Custom-event names dispatched on `window` so AuthContext can react to auth
 *  state changes from an arbitrary API call's 401-refresh path without
 *  importing AuthContext (which would create a cycle). */
export const AUTH_EVENTS = {
  refreshed: 'la:auth-refreshed',
  cleared: 'la:auth-cleared',
} as const;

export interface AuthRefreshedDetail {
  user: AuthUser;
}

// Single in-flight refresh promise per tab. Multiple concurrent 401s share the
// same refresh — without this they'd each send their own /auth/refresh, the
// first would rotate the cookie, and the rest would trip reuse-detection.
let refreshInFlight: Promise<void> | null = null;

function refreshOnce(): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = withRefreshLock(async () => {
    const res = await postRefresh();
    setAccessToken(res.data.tokens.accessToken);
    window.dispatchEvent(
      new CustomEvent<AuthRefreshedDetail>(AUTH_EVENTS.refreshed, {
        detail: { user: res.data.user },
      }),
    );
  })
    .catch((err) => {
      setAccessToken(null);
      window.dispatchEvent(new CustomEvent(AUTH_EVENTS.cleared));
      throw err;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) {
    throw new ApiError(0, 'VITE_API_BASE_URL is not configured');
  }
  return `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
}

function applyAuth(init: RequestInit, token: string | null): RequestInit {
  const headers = new Headers(init.headers);
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, credentials: 'include', headers };
}

/**
 * Fetch with auto-auth + 401 retry. Returns the raw Response so callers can
 * handle non-JSON or non-2xx responses. For JSON convenience, use `apiJson()`.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = buildUrl(path);
  const firstAttempt = await fetch(url, applyAuth(init, getAccessToken()));
  if (firstAttempt.status !== 401) return firstAttempt;

  // 401 — try to refresh and replay once.
  try {
    await refreshOnce();
  } catch {
    return firstAttempt;
  }
  return fetch(url, applyAuth(init, getAccessToken()));
}

/** Convenience JSON helper. Throws ApiError on non-2xx. */
export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await apiFetch(path, init);
  let body: unknown = undefined;
  try {
    body = await res.json();
  } catch {
    /* non-JSON body */
  }
  if (!res.ok) {
    const message =
      (body &&
      typeof body === 'object' &&
      'message' in body &&
      typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}
